import { ReactElement, useEffect, useRef, useState } from "react";
import { Anchor } from "@twilio-paste/core/anchor";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { styled } from "@twilio-paste/core/styling-library";
import { ClearEditorPlugin } from "@twilio-paste/core/lexical-library";
import { ArrowDownIcon } from "@twilio-paste/icons/esm/ArrowDownIcon";
import { HelpText } from "@twilio-paste/core/help-text";
import { Theme } from "@twilio-paste/core/theme";
import { useForm, Controller } from "react-hook-formV7";

import {
  WiseOwlBlock,
  dataTestIds,
  trackingDetails,
  EmptyStateVariant,
  NewAiMessageStatus,
  WiseOwlChatProps,
  SendMessageFormValues,
  ChatMessageData,
  NewChatFormValues,
  ChatFormLabels,
  WiseOwlBlockType,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import { useScroll } from "@twiliointernal/org/twilio/e2-commons/shared/hooks";
import {
  EmptyState,
  CompositeChatComposer,
  ChatComposerPlugins,
} from "@twiliointernal/org/twilio/e2-commons/shared/ui";
import { LiveChatComposer } from "@twiliointernal/org/twilio/e2-commons/live-chat";

import { ChatLog, PreChat } from "../../components";
import { useWiseOwlContext } from "../../hooks";

import {
  lastMessageFromAuthor,
  hasMessages,
} from "../../components/blockRenderer/helpers";
import { copy, constants } from "./definitions";

const WiseOwlAppContainer = styled(Box)`
  .paste-theme-provider {
    height: 100%;
  }
`;

const WiseOwlChat = ({
  blocks,
  chatId,
  chatMessages,
  newAiMessageStatus = NewAiMessageStatus.None,
  loadingConfiguration,
  variantOptions,
  onStartNewChat,
  onSendChatMessage,
  disabled = false,
  styleConfiguration = constants.styleConfigurationDefaults,
}: WiseOwlChatProps): ReactElement => {
  const {
    handlers: { handleTrackEvent },
  } = useWiseOwlContext();
  const wiseOwlChatContainer = document.getElementById(
    constants.WISE_OWL_CHAT_LOG_CONTAINER_ID
  );
  const { maxScroll, currentScroll, scrollToBottom, updateScrollState } =
    useScroll(wiseOwlChatContainer);
  const [isChatActive, setIsChatActive] = useState<boolean>(false);
  const [showNewMessageCta, setShowNewMessageCta] = useState<boolean>(false);
  const autoScrollOnAiMessage = useRef<boolean>(true);

  // form and field initialization
  const { handleSubmit, control, watch, formState, reset, setValue } =
    useForm<SendMessageFormValues>();
  const { isValid, isSubmitting, errors, isSubmitted } = formState;
  const inputHasMinLengthError = errors?.input?.type === "minLength";
  const inputFieldValue = watch("input");
  const chatIdFieldValue = watch("chatId");
  const newAiMessageComplete =
    newAiMessageStatus === NewAiMessageStatus.Complete;
  const newAiMessageLoading = newAiMessageStatus === NewAiMessageStatus.Loading;

  // WOBK - integrates chatId state as form value for handling sending message.
  useEffect(() => {
    // only set this value once
    if (!chatIdFieldValue && !!chatId) {
      setValue("chatId", chatId);
    }
  }, [chatId]);

  /**
   * Validates chat message input only on submit
   */
  useEffect(() => {
    if (isSubmitted) {
      reset(undefined, { keepDirtyValues: true });
    }
  }, [inputFieldValue]);

  /**
   * - Toggle New Message CTA based on scroll position and
   *  new message status
   * - Toggle Auto-scroll
   */
  useEffect(() => {
    if (!isUserViewingLatestMessages() && newAiMessageComplete) {
      setShowNewMessageCta(true);
    } else {
      toggleAutoScroll();
    }
  }, [newAiMessageStatus]);

  /**
   * - Hide New Message CTA if viewing new messages
   * - Toggle Auto-scroll
   */
  useEffect(() => {
    if (isUserViewingLatestMessages()) {
      setShowNewMessageCta(false);
    }
    toggleAutoScroll();
  }, [currentScroll]);

  /**
   * useEffect that trips on various
   * loading conditions
   */
  useEffect(() => {
    if (loadingConfiguration.fetchChatById) {
      setIsChatActive(true);
    }
  }, [loadingConfiguration]);

  /**
   * Set is chat active and call scroll/New Message CTA handlers
   * based on incoming blocks and legacy chat messages
   */
  useEffect(() => {
    updateScrollState();
    if (blocks.length) {
      setIsChatActive(hasMessages(blocks));
      handleScrollToBottom(blocks);
    } else if (chatMessages?.length) {
      setIsChatActive(true);
      handleLegacyChatMessages(chatMessages);
    } else {
      setIsChatActive(false);
    }
  }, [blocks, chatMessages, maxScroll]);

  // scroll to bottom if last message was from author / if AI
  // auto-scrolling is engaged.
  const handleScrollToBottom = (blocks: WiseOwlBlock[]): void => {
    if (lastMessageFromAuthor(blocks) || autoScrollOnAiMessage.current) {
      scrollToBottom();
      setShowNewMessageCta(false);
    }
  };

  // Handle scrolling and showing New Messages CTA for chat messages (legacy)
  const handleLegacyChatMessages = (chatMessages: ChatMessageData[]): void => {
    const { author } = chatMessages[chatMessages.length - 1];
    if (!author.isAuthor) {
      setShowNewMessageCta(isUserViewingLatestMessages() ? false : true);
    } else {
      scrollToBottom();
    }
  };

  /**
   * CTA Handlers
   */
  const handleMessageSentTrackEvent = (eventName: string): void => {
    if (handleTrackEvent) {
      handleTrackEvent(eventName, { chatId });
    }
  };

  const handleOnStartNewChat = (data: NewChatFormValues): void => {
    if (onStartNewChat) {
      onStartNewChat(data);
    }

    handleMessageSentTrackEvent(trackingDetails.wiseOwlChatCreated);
  };

  const handleOnSendChatMessage = (data: SendMessageFormValues): void => {
    if (onSendChatMessage) {
      // the legacy (no blocks) implementation of the API does not require
      // passing in chatId
      onSendChatMessage(chatId ? { ...data, chatId } : data);
    }

    handleMessageSentTrackEvent(trackingDetails.wiseOwlMessageSent);
  };

  const handleClickNewMessage = () => {
    scrollToBottom();
    setShowNewMessageCta(false);
  };

  const formHandler = isChatActive
    ? handleOnSendChatMessage
    : handleOnStartNewChat;

  const isUserViewingLatestMessages = () => {
    // if the chat log has no overflow, by default user is viewing latest messages
    if (currentScroll === 0 && maxScroll === 0) {
      return true;
    }

    return currentScroll / maxScroll > constants.VIEWING_LATEST_THRESHOLD;
  };

  const toggleAutoScroll = () => {
    if (isUserViewingLatestMessages()) {
      autoScrollOnAiMessage.current = newAiMessageLoading;
      setShowNewMessageCta(false);
    } else {
      autoScrollOnAiMessage.current = false;
    }
  };

  const renderScrollToNewMessageButton = () => (
    <Box
      display="flex"
      position="absolute"
      bottom="15%"
      left="50%"
      transform="translate(-50%, -50%)"
      zIndex="zIndex10"
    >
      <Button
        variant="primary"
        size="rounded_small"
        onClick={handleClickNewMessage}
      >
        {copy.ctas.newMessage}
        <ArrowDownIcon decorative />
      </Button>
    </Box>
  );

  const renderStateVariant = () => {
    if (!variantOptions) {
      return;
    }

    const { variant, ctaText, ctaUrl } = variantOptions;

    if (variant === EmptyStateVariant.oops) {
      return (
        <EmptyState
          variant={variant}
          heading={copy.variants.oopsTitle}
          description={copy.variants.oopsDescription}
        >
          <Anchor href={ctaUrl}>{ctaText}</Anchor>
        </EmptyState>
      );
    }
  };

  // should only render if we're not at the bottom and there's a new AI message
  // override if we're too close to bottom
  const shouldRenderScrollToNewMessageButton = isUserViewingLatestMessages()
    ? false
    : showNewMessageCta && currentScroll < maxScroll;

  const isLiveChatActive = () => {
    // TODO: Temporal test... this shoudl be replaced with the liveChatId from Chat Data model
    const liveChatBlock = blocks.find(
      (block) => block.type === WiseOwlBlockType.LiveChat
    );
    return !!liveChatBlock;
  };

  const onChatError = (error: Error) => {
    throw error;
  };

  const renderChatComposer = (): ReactElement => {
    return isLiveChatActive() ? (
      <LiveChatComposer onError={onChatError} />
    ) : (
      <Controller
        name={ChatFormLabels.input}
        control={control}
        rules={{ required: true, minLength: 10 }}
        render={({ field }) => (
          <CompositeChatComposer
            config={{
              namespace: "comms-gpt-message",
              onError: onChatError,
            }}
            disabled={disabled}
            ariaLabel={copy.fields.input.ariaLabel}
            placeholder={copy.fields.input.placeholder}
            required
            {...field}
            data-testid={dataTestIds.wiseOwlChat.chatComposer}
          >
            <ClearEditorPlugin />
            <ChatComposerPlugins.ChatSendButtonPlugin
              loading={loadingConfiguration.submission}
              isChatActive={isChatActive}
              shouldClearInput={isValid && isSubmitting}
              disabled={disabled}
              handleOnEnter={handleSubmit(formHandler)}
              handleOnClick={handleSubmit(formHandler)}
            />
          </CompositeChatComposer>
        )}
      />
    );
  };

  return (
    <WiseOwlAppContainer as="div" style={{ height: "100%" }}>
      <Theme.Provider theme="twilio">
        <Box as="form" height="inherit" onSubmit={handleSubmit(formHandler)}>
          {variantOptions && renderStateVariant()}

          {!variantOptions && (
            <Box
              display="flex"
              flexDirection="column"
              position="relative"
              width="100%"
              height="100%"
              maxWidth="size70"
              marginX="auto"
            >
              {/* Chat Container (pre/active chat) */}
              <Box
                as="section"
                flexGrow={1}
                id={constants.WISE_OWL_CHAT_LOG_CONTAINER_ID}
                overflowX="hidden"
                overflowY={isChatActive ? "auto" : undefined}
                tabIndex={0}
              >
                <Box>
                  {shouldRenderScrollToNewMessageButton &&
                    renderScrollToNewMessageButton()}

                  {isChatActive ? (
                    <ChatLog
                      blocks={blocks}
                      chatMessages={chatMessages}
                      formatDatePastYesterday={
                        constants.DATE_FORMAT_AFTER_YESTERDAY
                      }
                    />
                  ) : (
                    <PreChat />
                  )}
                </Box>
              </Box>

              <Box
                as="section"
                position="relative"
                maxWidth="size70"
                width="100%"
                marginX="auto"
              >
                {/* Message input */}
                <Box as="article" position="relative" paddingBottom="space50">
                  {renderChatComposer()}
                  {isSubmitted && inputHasMinLengthError && (
                    <HelpText variant="error">
                      {copy.errors.messageMinLength}
                    </HelpText>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Theme.Provider>
    </WiseOwlAppContainer>
  );
};

export default WiseOwlChat;
